import {
    useState,
    useRef,
    useCallback,
    useImperativeHandle,
    useEffect
} from 'react'
import {
    TransformWrapper,
    TransformComponent
} from 'react-zoom-pan-pinch'
import Field from './Field'
import { generateDocImage } from './generateDocImage'

function buildInitialValues(fields = []) {
    const initial = {}
    fields.forEach((f, i) => {
        if (f.default !== undefined && f.default !== null && f.default !== '') {
            initial[`field_${i}`] = f.default
        }
    })
    return initial
}

export default function Doc({
    doc,
    ref
}) {
    const [originalSize, setOriginalSize] = useState({ width: 0, height: 0 })
    const [values, setValues] = useState(() => buildInitialValues(doc.fields))
    const imgRef = useRef(null)

    const handleImageLoad = (e) => {
        setOriginalSize({
            width: e.target.naturalWidth,
            height: e.target.naturalHeight,
        })
    }

    const setValue = useCallback((key, value) => {
        setValues((prev) => ({ ...prev, [key]: value }))
    }, [])

    const getFieldStyle = (x, y, width, height) => {
        if (originalSize.width === 0) return { display: 'none' }
        return {
            position: 'absolute',
            left: `${(x / originalSize.width) * 100}%`,
            top: `${(y / originalSize.height) * 100}%`,
            width: `${(width / originalSize.width) * 100}%`,
            height: `${(height / originalSize.height) * 100}%`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }
    }

    const fontSizeCqw = (fontSizePx) => {
        if (!originalSize.width) return '1cqw'
        return `${(fontSizePx / originalSize.width) * 75}cqw`
    }

    const handlePointerDown = (e) => {
        const active = document.activeElement
        if (!active) return
        const isEditable = active.tagName === 'INPUT' || active.tagName === 'TEXTAREA'
        if (isEditable && e.target !== active) {
            active.blur()
        }
    }

    useImperativeHandle(ref, () => ({
        generate: () => generateDocImage(doc, values, imgRef.current),
        values,
    }), [doc, values])

    return (
        <div
            onPointerDown={handlePointerDown}
            style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                userSelect: 'none',
                touchAction: 'none',
                backgroundColor: '#f5f5f5'
            }}
        >
            <TransformWrapper
                initialScale={1}
                centerOnInit
                centerZoomedOut
                limitToBounds
                disablePadding
                minScale={.8}
                maxScale={8}
                wheel={{ step: 0.001 }}
                doubleClick={{ disabled: true }}
            >
                <TransformComponent
                    wrapperStyle={{ width: '100%', height: '100%', overflow: 'hidden' }}
                    contentStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            display: 'inline-block',
                            width: 'fit-content',
                            height: 'fit-content',
                        }}
                    >
                        <img
                            ref={imgRef}
                            src={doc.image}
                            alt=''
                            draggable={false}
                            onLoad={handleImageLoad}
                            style={{
                                display: 'block',
                                maxWidth: '100%',
                                height: 'auto',
                                userSelect: 'none',
                                WebkitUserDrag: 'none',
                                pointerEvents: 'none',
                            }}
                        />
                            <div style={{ position: 'absolute', inset: 0 }}>
                                {doc?.fields?.map((f, i) => {
                                    const key = `field_${i}`
                                    return (
                                        <div
                                            key={key}
                                            style={getFieldStyle(f.x, f.y, f.w, f.h)}
                                        >
                                            <Field
                                                type={f.type}
                                                value={values[key]}
                                                onChange={(v) => setValue(key, v)}
                                                fontSize={fontSizeCqw(f.fontSize ?? 14)}
                                                readonly={f.readonly}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                    </div>
                </TransformComponent>
            </TransformWrapper>
        </div>
    )
}